import MoreVertIcon from '@mui/icons-material/MoreVert';
import PreviewOutlinedIcon from '@mui/icons-material/PreviewOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    DialogContent,
    FormControl,
    IconButton,
    InputAdornment,
    LinearProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { ContentInventory, InventoryVersion } from '../../hooks/useInventory';
import { stripHtml } from '../../utils/common';
import { OnlyFansVaultMedias } from '../../hooks/useSubscriberVaultAlbums';
import useSubscribersVaultMediasAdditional from '../../hooks/useSubscriberVaultMediasAdditional';
import MessageUsersFormEditiorPreview from '../services/messageUsers/sendMessage/MessageUsersFormEditiorPreview';
import DialogAdaptive from './DialogAdaptive';
import StyledCardTitleBar from './StyledCardTitleBar';
import StyledDialogActions from './StyledDialogActions';
import { readInventoryTemplateSortAlphabetically, writeInventoryTemplateSortAlphabetically } from './inventoryTemplateSortPreference';
import { InventoryTemplateSelection, reconcileInventoryTemplateSelections } from './inventoryTemplateSelection';

export type { InventoryTemplateSelection } from './inventoryTemplateSelection';

type Props = {
    inventory: ContentInventory[] | undefined;
    loading?: boolean;
    selectedTemplates: InventoryTemplateSelection[];
    onChange: (nextTemplates: InventoryTemplateSelection[]) => void;
    height?: number;
};

type VariantOption = {
    type: 'variant';
    key: string;
    inventoryId: number;
    inventoryName: string;
    versionId: number;
    versionName: string;
    tags: string[];
    isPaid: boolean;
};

type GroupOption = {
    type: 'group';
    key: string;
    inventoryId: number;
    inventoryName: string;
};

type FlatOption = GroupOption | VariantOption;

const HEADER_ROW_HEIGHT = 34;
const SORT_ALPHABETICALLY_STORAGE_KEY = 'inventoryTemplateVariantsSelector.sortAlphabetically';

const readSortAlphabeticallyFromStorage = (): boolean => {
    try {
        const stored = localStorage.getItem(SORT_ALPHABETICALLY_STORAGE_KEY);
        if (stored === null) {
            return true;
        }
        return stored === 'true';
    } catch {
        return true;
    }
};

type PreviewState = {
    inventoryName: string;
    version: InventoryVersion;
} | null;

const InventoryTemplateVariantsSelector = (props: Props) => {
    const { inventory, loading = false, selectedTemplates, onChange, height = 320 } = props;
    const theme = useTheme();
    const [search, setSearch] = useState('');
    const [sortAlphabetically, setSortAlphabetically] = useState(readInventoryTemplateSortAlphabetically);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [preview, setPreview] = useState<PreviewState>(null);
    const [previewLockedMedia, setPreviewLockedMedia] = useState<OnlyFansVaultMedias.RestructuredResponseItem[]>([]);
    const { getManyVaultMediaFromDatabase } = useSubscribersVaultMediasAdditional();

    const normalizedSearch = search.trim().toLowerCase();

    // Saved selections can reference templates/variations that were deleted or deactivated since
    // the selection was stored, contain duplicates, or carry string ids — none of which can render
    // as a checked row, so the "N selected" count and the checkboxes disagree. Reconcile against
    // the loaded inventory and push the cleaned list back up so count, checkboxes, and whatever
    // gets saved all agree. reconcile reports changed=false on a clean list, so this cannot loop.
    useEffect(() => {
        if (loading || !inventory) {
            return;
        }
        const { reconciled, changed } = reconcileInventoryTemplateSelections(inventory, selectedTemplates);
        if (changed) {
            onChange(reconciled);
        }
    }, [loading, inventory, selectedTemplates, onChange]);

    // Built separately from the search filter so the HTML stripping of each version's text runs only
    // when the inventory changes, not on every keystroke.
    const variantGroups = useMemo(() => {
        const inventoryList = inventory || [];
        const displayInventory = sortAlphabetically ? [...inventoryList].sort((a, b) => a.name.localeCompare(b.name)) : inventoryList;

        return displayInventory.map(inv => ({
            inventoryId: inv.inventoryId,
            inventoryName: inv.name,
            variants: (inv.versions || [])
                .filter(v => v.status === 'ACTIVE')
                .map(v => {
                    const tags = (v.tags || []).map(t => t.tagText).filter(Boolean);
                    const option: VariantOption = {
                        type: 'variant',
                        key: `${inv.inventoryId}:${v.inventorySubId}`,
                        inventoryId: inv.inventoryId,
                        inventoryName: inv.name,
                        versionId: v.inventorySubId,
                        versionName: v.versionName,
                        tags,
                        isPaid: Number(v.price || 0) > 0,
                    };
                    const searchText = `${inv.name} ${v.versionName} ${tags.join(' ')} ${
                        v.text ? stripHtml(v.text) : ''
                    }`.toLowerCase();
                    return { option, searchText };
                }),
        }));
    }, [inventory, sortAlphabetically]);

    const options = useMemo(() => {
        const list: FlatOption[] = [];

        variantGroups.forEach(group => {
            const filtered = !normalizedSearch ? group.variants : group.variants.filter(v => v.searchText.includes(normalizedSearch));

            if (filtered.length > 0) {
                list.push({
                    type: 'group',
                    key: `group:${group.inventoryId}`,
                    inventoryId: group.inventoryId,
                    inventoryName: group.inventoryName,
                });
                list.push(...filtered.map(v => v.option));
            }
        });

        return list;
    }, [variantGroups, normalizedSearch]);

    const toggleSortAlphabetically = () => {
        setSortAlphabetically(prev => {
            const next = !prev;
            writeInventoryTemplateSortAlphabetically(next);
            return next;
        });
    };

    const previewLockedMediaIdsKey = useMemo(() => {
        if (!preview) {
            return '';
        }

        return (preview.version.lockedMedias ?? [])
            .slice()
            .sort((a, b) => a.order - b.order)
            .map(media => media.mediaId)
            .join(',');
    }, [preview]);

    useEffect(() => {
        let isMounted = true;

        const fetchLockedMedia = async () => {
            if (!previewLockedMediaIdsKey) {
                if (isMounted) {
                    setPreviewLockedMedia([]);
                }
                return;
            }

            const lockedMediaIds = previewLockedMediaIdsKey.split(',').map(Number);
            const medias = await getManyVaultMediaFromDatabase(lockedMediaIds);
            if (isMounted) {
                setPreviewLockedMedia(medias ?? []);
            }
        };

        void fetchLockedMedia();

        return () => {
            isMounted = false;
        };
    }, [previewLockedMediaIdsKey]);

    const openPreview = (row: VariantOption, event: React.MouseEvent) => {
        event.stopPropagation();

        const inv = inventory?.find(entry => entry.inventoryId === row.inventoryId);
        const version = inv?.versions.find(entry => entry.inventorySubId === row.versionId);

        if (version) {
            setPreview({ inventoryName: row.inventoryName, version });
        }
    };

    const toggleInventorySelection = (selection: InventoryTemplateSelection, checked: boolean) => {
        const key = `${selection.inventoryId}:${selection.versionId}`;
        if (checked) {
            const exists = selectedTemplates.some(s => `${s.inventoryId}:${s.versionId}` === key);
            if (!exists) onChange([...selectedTemplates, selection]);
            return;
        }
        onChange(selectedTemplates.filter(s => `${s.inventoryId}:${s.versionId}` !== key));
    };

    const visibleVariants = useMemo(() => options.filter((o): o is VariantOption => o.type === 'variant'), [options]);

    const setSelection = (nextSelections: InventoryTemplateSelection[]) => {
        const unique = new Map<string, InventoryTemplateSelection>();
        nextSelections.forEach(s => {
            unique.set(`${s.inventoryId}:${s.versionId}`, s);
        });
        onChange(Array.from(unique.values()));
    };

    const renderVariantRow = (row: VariantOption) => {
        const checked = selectedTemplates.some(s => s.inventoryId === row.inventoryId && s.versionId === row.versionId);
        const selection = { inventoryId: row.inventoryId, versionId: row.versionId };

        return (
            <Box
                key={row.key}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 52,
                    px: 1,
                    py: 0.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:hover': { backgroundColor: 'action.hover' },
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', minWidth: 0 }}>
                    <Checkbox
                        size="small"
                        checked={checked}
                        sx={{ p: 0.25, flexShrink: 0 }}
                        onChange={e => toggleInventorySelection(selection, e.target.checked)}
                    />
                    <Box
                        sx={{ minWidth: 0, flex: 1, overflow: 'hidden', cursor: 'pointer' }}
                        onClick={() => toggleInventorySelection(selection, !checked)}
                    >
                        <Typography
                            variant="body2"
                            noWrap
                            title={row.versionName}
                            sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {row.versionName}
                        </Typography>
                        {row.tags.length > 0 && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                title={row.tags.slice(0, 5).join(' • ')}
                                sx={{
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {row.tags.slice(0, 5).join(' • ')}
                            </Typography>
                        )}
                    </Box>
                    {row.isPaid && <Chip size="small" label="Paid" color="warning" variant="outlined" sx={{ ml: 1, flexShrink: 0 }} />}
                    <IconButton
                        size="small"
                        aria-label="Preview"
                        onClick={e => openPreview(row, e)}
                        sx={{
                            display: { xs: 'inline-flex', sm: 'none' },
                            ml: 0.5,
                            flexShrink: 0,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                        }}
                    >
                        <PreviewOutlinedIcon fontSize="small" />
                    </IconButton>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PreviewOutlinedIcon />}
                        sx={{ display: { xs: 'none', sm: 'inline-flex' }, ml: 0.5, flexShrink: 0 }}
                        onClick={e => openPreview(row, e)}
                    >
                        Preview
                    </Button>
                </Stack>
            </Box>
        );
    };

    const previewPrice = preview ? Number(preview.version.price || 0) : 0;
    const previewTaggedCreators =
        preview?.version.creatorTags?.map(creator => ({
            id: creator.creatorOnlyfansId,
            name: creator.creatorName,
        })) ?? [];
    const previewTaggedReleaseForms =
        preview?.version.additionalSettings?.consentForms?.map(form => ({
            id: form.id,
            name: form.name,
        })) ?? [];
    const previewUnlockedMediaIds =
        preview?.version.unlockedMedias
            ?.slice()
            .sort((a, b) => a.order - b.order)
            .map(media => media.mediaId) ?? [];

    return (
        <>
            <FormControl fullWidth>
                <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Search by template name, version, tag, or text..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ flex: 1 }}
                    />
                    <IconButton
                        size="small"
                        onClick={e => setMenuAnchorEl(e.currentTarget)}
                        aria-label="template selection options"
                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </Stack>
                <Menu
                    anchorEl={menuAnchorEl}
                    open={!!menuAnchorEl}
                    onClose={() => setMenuAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <MenuItem
                        selected={sortAlphabetically}
                        onClick={() => {
                            toggleSortAlphabetically();
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <SortByAlphaIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Sort alphabetically</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            setSelection([]);
                            setMenuAnchorEl(null);
                        }}
                    >
                        Select None
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            setSelection(
                                visibleVariants.map(v => ({
                                    inventoryId: v.inventoryId,
                                    versionId: v.versionId,
                                })),
                            );
                            setMenuAnchorEl(null);
                        }}
                    >
                        Select All
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            setSelection(
                                visibleVariants
                                    .filter(v => v.isPaid)
                                    .map(v => ({
                                        inventoryId: v.inventoryId,
                                        versionId: v.versionId,
                                    })),
                            );
                            setMenuAnchorEl(null);
                        }}
                    >
                        Select All Paid
                    </MenuItem>
                </Menu>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {selectedTemplates.length.toLocaleString()} selected
                </Typography>
                {loading ? (
                    <LinearProgress />
                ) : options.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No templates found.
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            height,
                            overflow: 'auto',
                        }}
                    >
                        {options.map(row =>
                            row.type === 'group' ? (
                                <Box
                                    key={row.key}
                                    sx={{
                                        px: 1.25,
                                        py: 0.75,
                                        minHeight: HEADER_ROW_HEIGHT,
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        backgroundColor: 'action.hover',
                                        borderBottom: 1,
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        noWrap
                                        title={row.inventoryName}
                                        sx={{
                                            display: 'block',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {row.inventoryName}
                                    </Typography>
                                </Box>
                            ) : (
                                renderVariantRow(row)
                            ),
                        )}
                    </Box>
                )}
            </FormControl>
            <DialogAdaptive open={!!preview} onClose={() => setPreview(null)} fullWidth maxWidth="sm">
                <StyledCardTitleBar
                    title={`Message Preview - ${preview?.inventoryName || 'Template'} - ${preview?.version.versionName || 'Version'}`}
                />
                <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
                    {preview && (
                        <MessageUsersFormEditiorPreview
                            message={preview.version.text || ''}
                            taggedCreators={previewTaggedCreators}
                            taggedReleaseForms={previewTaggedReleaseForms}
                            mediaFiles={previewLockedMedia}
                            mediaFilesPreviews={previewUnlockedMediaIds}
                            lockMessage={previewPrice > 0}
                            lockMessagePrice={previewPrice > 0 ? previewPrice : 0}
                            lockedText={false}
                            theme={theme}
                            fullWidth
                            showTags
                            showTime
                            showMedia
                        />
                    )}
                </DialogContent>
                <StyledDialogActions>
                    <Button onClick={() => setPreview(null)} color="primary">
                        Close
                    </Button>
                </StyledDialogActions>
            </DialogAdaptive>
        </>
    );
};

export default InventoryTemplateVariantsSelector;
